import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import PersianDate from "persian-date";
import {
  isoToJalali,
  jalaliToISO,
  jalaliDaysInMonth,
  parseJalaliParts,
  toPersianDigits,
} from "./jalaliUtils";

const JALALI_MONTHS = [
  "فروردین",
  "اردیبهشت",
  "خرداد",
  "تیر",
  "مرداد",
  "شهریور",
  "مهر",
  "آبان",
  "آذر",
  "دی",
  "بهمن",
  "اسفند",
];

const WEEK_DAYS = ["ش", "ی", "د", "س", "چ", "پ", "ج"];
const pad2 = (n) => String(n).padStart(2, "0");
const POPUP_GAP = 6;
const VIEWPORT_MARGIN = 8;
const POPUP_WIDTH_DESKTOP = 264;

function jalaliToParts(jalali) {
  const p = parseJalaliParts(jalali);
  if (p) return p;
  return null;
}

/**
 * Iranian/Jalali date-picker popup.
 * - Displays the date in Jalali with Persian digits (e.g. ۱۴۰۴/۰۶/۱۲).
 * - Internal value / onChange payload is Gregorian ISO "YYYY-MM-DD" (backend-safe).
 * - Accepts ISO or legacy Jalali strings as `value`.
 * - Popup is portalled to document.body with fixed, viewport-clamped
 *   positioning so it never stretches the edit modal / page scroll.
 * - Position is computed once on open and frozen while open: scroll,
 *   month navigation, re-renders, and resize never move it.
 */
function ServiceDatePicker({
  value = "",
  onChange,
  label = "تاریخ سرویس",
  placeholder = "تاریخ را انتخاب کنید",
  disabled = false,
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [view, setView] = useState(() => {
    const jalali = isoToJalali(value);
    const p = jalali ? jalaliToParts(jalali) : null;
    if (p) return { y: p.y, m: p.m };
    const now = new PersianDate();
    return { y: now.year(), m: now.month() };
  });
  // Fixed viewport position for the portalled popup (no modal scroll impact).
  const [pos, setPos] = useState(null);

  const fieldRef = useRef(null);
  const anchorRef = useRef(null);
  const popupRef = useRef(null);
  const inputRef = useRef(null);
  // Set when closing programmatically (select / today / × / Escape) so the
  // focus we return to the input doesn't instantly re-trigger onFocus-open.
  const suppressFocusOpenRef = useRef(false);

  const jalaliValue = useMemo(() => isoToJalali(value), [value]);
  const displayValue = jalaliValue ? toPersianDigits(jalaliValue) : "";
  const todayJalali = useMemo(() => {
    const now = new PersianDate();
    return `${now.year()}/${pad2(now.month())}/${pad2(now.date())}`;
  }, []);

  // Keep calendar view in sync when parent value changes.
  useEffect(() => {
    if (!jalaliValue) return;
    const p = jalaliToParts(jalaliValue);
    if (p) setView({ y: p.y, m: p.m });
  }, [jalaliValue]);

  // NOTE: open-only on purpose. A previous toggle-on-click raced with
  // onFocus-open (focus fired open, then click toggled closed), so the
  // popup opened and instantly closed. It now stays open until the user
  // picks a day, hits "امروز", clicks outside, presses Escape, or uses ×.
  const openPopup = useCallback(() => {
    if (disabled) return;
    setIsOpen(true);
  }, [disabled]);

  const closePopup = useCallback(() => {
    setIsOpen(false);
  }, []);

  const focusInputWithoutReopen = useCallback(() => {
    suppressFocusOpenRef.current = true;
    inputRef.current?.focus?.();
  }, []);

  const handleInputFocus = useCallback(() => {
    // Consume the focus that follows a programmatic close (day select,
    // "امروز", ×, Escape) so the calendar doesn't instantly reopen.
    if (suppressFocusOpenRef.current) {
      suppressFocusOpenRef.current = false;
      return;
    }
    openPopup();
  }, [openPopup]);

  const handleInputBlur = useCallback(() => {
    suppressFocusOpenRef.current = false;
  }, []);

  // Viewport-clamped fixed position anchored to the input.
  // Computed EXACTLY ONCE per open and then frozen: scrolling, month
  // navigation, parent re-renders, resize, or RTL/layout shifts must never
  // move the popup while it is open (no jumping/shifting).
  const positionOnce = useCallback(() => {
    if (typeof window === "undefined") return;
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    const pw = Math.min(
      POPUP_WIDTH_DESKTOP,
      vw <= 480 ? 240 : POPUP_WIDTH_DESKTOP,
      Math.max(200, vw - VIEWPORT_MARGIN * 2)
    );
    const anchor = anchorRef.current?.getBoundingClientRect();
    if (!anchor) {
      setPos((prev) => ({ ...(prev || {}), width: pw, top: Math.max(VIEWPORT_MARGIN, vh / 2 - 160), left: Math.max(VIEWPORT_MARGIN, vw / 2 - pw / 2) }));
      return;
    }
    const ph = popupRef.current?.offsetHeight || 320;
    let left = anchor.left + anchor.width / 2 - pw / 2;
    left = Math.max(VIEWPORT_MARGIN, Math.min(left, vw - pw - VIEWPORT_MARGIN));
    const below = anchor.bottom + POPUP_GAP;
    const above = anchor.top - ph - POPUP_GAP;
    let top;
    if (below + ph <= vh - VIEWPORT_MARGIN) top = Math.max(below, VIEWPORT_MARGIN);
    else if (above >= VIEWPORT_MARGIN) top = above;
    else top = Math.max(VIEWPORT_MARGIN, Math.min(below, vh - ph - VIEWPORT_MARGIN));
    setPos({ top, left, width: pw });
  }, []);

  // Position once when the popup mounts (real height known, before paint
  // so there is no visible jump) and freeze it for the whole open session.
  // Deliberately NOT re-run on `view` (month nav), scroll, or resize.
  useLayoutEffect(() => {
    if (!isOpen) {
      setPos(null);
      return;
    }
    positionOnce();
  }, [isOpen, positionOnce]);

  // Close on outside pointer-down / focus-leave / Escape.
  // Both the field and the portalled popup count as "inside".
  useEffect(() => {
    if (!isOpen) return;
    const isInside = (target) => {
      if (!target) return false;
      if (fieldRef.current?.contains(target)) return true;
      if (popupRef.current?.contains(target)) return true;
      return false;
    };
    const onPointerDown = (e) => {
      if (!isInside(e.target)) setIsOpen(false);
    };
    const onFocusIn = (e) => {
      if (!isInside(e.target)) setIsOpen(false);
    };
    const onKey = (e) => {
      if (e.key === "Escape") {
        e.stopPropagation();
        setIsOpen(false);
        focusInputWithoutReopen();
      }
    };
    document.addEventListener("mousedown", onPointerDown);
    document.addEventListener("touchstart", onPointerDown, { passive: true });
    document.addEventListener("focusin", onFocusIn);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onPointerDown);
      document.removeEventListener("touchstart", onPointerDown);
      document.removeEventListener("focusin", onFocusIn);
      document.removeEventListener("keydown", onKey);
    };
  }, [isOpen, focusInputWithoutReopen]);

  const goMonth = (delta) => {
    setView((prev) => {
      let { y, m } = prev;
      m += delta;
      while (m < 1) {
        m += 12;
        y -= 1;
      }
      while (m > 12) {
        m -= 12;
        y += 1;
      }
      return { y, m };
    });
  };

  const pickDay = (day) => {
    const jalali = `${view.y}/${pad2(view.m)}/${pad2(day)}`;
    const iso = jalaliToISO(jalali);
    if (!iso) return;
    if (onChange) onChange(iso);
    setIsOpen(false);
    focusInputWithoutReopen();
  };

  const pickToday = () => {
    const iso = jalaliToISO(todayJalali);
    if (iso && onChange) onChange(iso);
    const p = jalaliToParts(todayJalali);
    if (p) setView({ y: p.y, m: p.m });
    setIsOpen(false);
    focusInputWithoutReopen();
  };

  const daysInMonth = jalaliDaysInMonth(view.y, view.m);
  let firstWeekDay = 0;
  try {
    firstWeekDay = new PersianDate([view.y, view.m, 1]).day();
  } catch {
    firstWeekDay = 0;
  }

  const cells = [];
  for (let i = 0; i < firstWeekDay; i += 1) {
    cells.push(<div key={`empty-${i}`} className="ls-date__day ls-date__day--empty" />);
  }
  for (let day = 1; day <= daysInMonth; day += 1) {
    const key = `${view.y}/${pad2(view.m)}/${pad2(day)}`;
    cells.push(
      <button
        key={day}
        type="button"
        className={[
          "ls-date__day",
          key === jalaliValue ? "is-selected" : "",
          key === todayJalali ? "is-today" : "",
        ].join(" ").trim()}
        onClick={() => pickDay(day)}
      >
        {toPersianDigits(day)}
      </button>
    );
  }

  const popupStyle =
    pos != null
      ? { top: pos.top, left: pos.left, width: pos.width }
      : { visibility: "hidden" };

  return (
    <div className="ls-date" ref={fieldRef} dir="rtl">
      {label && <span className="ls-date__label">{label}</span>}
      <div className="ls-date__input-wrap" ref={anchorRef}>
        <input
          ref={inputRef}
          type="text"
          className="ls-date__input"
          value={displayValue}
          placeholder={placeholder}
          readOnly
          disabled={disabled}
          onFocus={handleInputFocus}
          onBlur={handleInputBlur}
          onClick={openPopup}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " " || e.key === "ArrowDown") {
              e.preventDefault();
              openPopup();
            }
          }}
          aria-haspopup="dialog"
          role="combobox"
          aria-autocomplete="none"
          aria-expanded={isOpen}
          aria-controls={isOpen ? "ls-jalali-calendar" : undefined}
        />
        <span className="ls-date__icon" aria-hidden>
          <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
            <rect x="3" y="4" width="18" height="17" rx="2" />
            <path d="M16 2v4M8 2v4M3 10h18" />
          </svg>
        </span>
      </div>

      {isOpen &&
        !disabled &&
        typeof document !== "undefined" &&
        createPortal(
          <div
            className="ls-date__popup"
            id="ls-jalali-calendar"
            ref={popupRef}
            role="dialog"
            aria-label="انتخاب تاریخ شمسی"
            style={popupStyle}
            onMouseDown={(e) => e.stopPropagation()}
            onTouchStart={(e) => e.stopPropagation()}
          >
            <div className="ls-date__popup-head">
              <button type="button" className="ls-date__nav" onClick={() => goMonth(1)} aria-label="ماه بعد">
                <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="9 18 15 12 9 6" />
                </svg>
              </button>
              <div className="ls-date__month">
                <span>{JALALI_MONTHS[view.m - 1]}</span>
                <span>{toPersianDigits(view.y)}</span>
              </div>
              <button type="button" className="ls-date__nav" onClick={() => goMonth(-1)} aria-label="ماه قبل">
                <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="15 18 9 12 15 6" />
                </svg>
              </button>
              <button
                type="button"
                className="ls-date__close"
                onClick={() => {
                  closePopup();
                  focusInputWithoutReopen();
                }}
                aria-label="بستن تقویم"
              >
                <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                  <path d="M18 6 6 18M6 6l12 12" />
                </svg>
              </button>
            </div>

            <div className="ls-date__week">
              {WEEK_DAYS.map((d) => (
                <div key={d} className="ls-date__weekday">
                  {d}
                </div>
              ))}
            </div>
            <div className="ls-date__grid">{cells}</div>

            <div className="ls-date__foot">
              <button type="button" className="ls-date__today" onClick={pickToday}>
                امروز
              </button>
              {jalaliValue && (
                <span className="ls-date__current">{toPersianDigits(jalaliValue)}</span>
              )}
            </div>
          </div>,
          document.body
        )}
    </div>
  );
}

export default ServiceDatePicker;
